import type { ReactNode } from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { DashboardSnapshot } from '../lib/api'

export function MetricsCharts({ data }: { data: DashboardSnapshot }) {
  const grades = data.graphs.mean_skill_grade_by_build.map((row, i) => ({
    name: `B${i + 1}`,
    grade: Number(row.mean_grade || 0),
    full: row.build_id,
  }))
  const mvp = data.graphs.mvp_ready_by_build.map((row, i) => ({
    name: `B${i + 1}`,
    ready: row.mvp_ready,
    full: row.build_id,
  }))
  const funnel = (data.graphs.sdlc_funnel || []).map((row) => ({
    name: row.stage.replace(/_/g, ' '),
    count: row.count,
  }))

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <ChartPanel title="Mean skill grade by build" subtitle="Learning agent quality signal">
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={grades}>
            <defs>
              <linearGradient id="gradeFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2EE6C5" stopOpacity={0.55} />
                <stop offset="100%" stopColor="#2EE6C5" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(158,190,255,0.12)" vertical={false} />
            <XAxis dataKey="name" stroke="#9db0d4" tickLine={false} axisLine={false} />
            <YAxis domain={[0, 5]} stroke="#9db0d4" tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{
                background: '#101a33',
                border: '1px solid rgba(158,190,255,0.2)',
                borderRadius: 12,
              }}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.full || ''}
            />
            <Area type="monotone" dataKey="grade" stroke="#2EE6C5" fill="url(#gradeFill)" strokeWidth={2.5} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartPanel>

      <ChartPanel title="MVP readiness" subtitle="1 = ready · 0 = still looping">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={mvp}>
            <CartesianGrid stroke="rgba(158,190,255,0.12)" vertical={false} />
            <XAxis dataKey="name" stroke="#9db0d4" tickLine={false} axisLine={false} />
            <YAxis domain={[0, 1]} stroke="#9db0d4" tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{
                background: '#101a33',
                border: '1px solid rgba(158,190,255,0.2)',
                borderRadius: 12,
              }}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.full || ''}
            />
            <Bar dataKey="ready" fill="#FFB347" radius={[10, 10, 4, 4]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartPanel>

      {funnel.length > 0 && (
        <ChartPanel
          title="SDLC factory funnel"
          subtitle="Ticket → discovery → workforce → ephemeral → merge gate"
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={funnel}>
              <CartesianGrid stroke="rgba(158,190,255,0.12)" vertical={false} />
              <XAxis dataKey="name" stroke="#9db0d4" tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} stroke="#9db0d4" tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{
                  background: '#101a33',
                  border: '1px solid rgba(158,190,255,0.2)',
                  borderRadius: 12,
                }}
              />
              <Bar dataKey="count" fill="#7C9CFF" radius={[10, 10, 4, 4]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      )}
    </div>
  )
}

function ChartPanel({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle: string
  children: ReactNode
}) {
  return (
    <div className="glass rounded-3xl p-5">
      <div className="mb-4">
        <h3 className="font-[family-name:var(--font-display)] text-lg font-bold">{title}</h3>
        <p className="text-sm text-[var(--color-mist)]">{subtitle}</p>
      </div>
      {children}
    </div>
  )
}
